import { FlowButton } from 'components/ui/flow-button';
import { useMemo, useState } from 'react';

import { CheckCircle2, Loader2 } from 'lucide-react';
import { useReveal } from 'hooks/useReveal';
import { readApplicationDraft, clearApplicationDraft, loadAssessmentAnswers, saveAssessmentAnswers, submitJobApplicationAssessment } from 'lib/sitePublicApi';
import {
  PAGE_HERO_BODY_TEXT_CLASS,
  PAGE_HERO_CONTAINER_CLASS,
  PAGE_HERO_EYEBROW_CLASS,
  PAGE_HERO_H1_CLASS,
  PAGE_HERO_SECTION_CLASS,
} from 'components/SiteHeroCtas';
import { toast } from 'sonner';

const CAREERS_ASSESSMENT_STORAGE_KEY = 'magellanCareersAssessmentDraft';

const EXPERIENCE_QUESTIONS = [
  {
    id: 'experienceYears',
    label: 'How many total years of professional work experience do you have, and how many of those years are connected to logistics, transportation, recruiting, sales support, dispatch, or operations?',
    type: 'textarea',
    placeholder: 'Explain total years, industry years, and the type of work.',
  },
  {
    id: 'lastRole',
    label: 'What was your most recent job title, who did you work for, and what were your exact day-to-day responsibilities?',
    type: 'textarea',
    placeholder: 'Describe your most recent role in detail.',
  },
  {
    id: 'pastCompanies',
    label: 'List the last three companies you worked for, how long you stayed at each one, and why you left.',
    type: 'textarea',
    placeholder: 'Company, duration, reason for leaving.',
  },
  {
    id: 'toolsUsed',
    label: 'What software, CRM, TMS, communication tools, spreadsheets, or internal systems have you actually used at work?',
    type: 'textarea',
    placeholder: 'Name the tools and what you used them for.',
  },
  {
    id: 'metricsOwnership',
    label: 'What numbers, KPIs, quotas, service levels, or operational outcomes were you personally responsible for?',
    type: 'textarea',
    placeholder: 'Be specific about targets and outcomes.',
  },
  {
    id: 'difficultSituation',
    label: 'Describe one difficult work situation you handled well and one you handled badly. What did you learn from both?',
    type: 'textarea',
    placeholder: 'Use real situations only.',
  },
  {
    id: 'shiftReadiness',
    label: 'What shifts, time zones, weekends, or availability restrictions do you currently have?',
    type: 'textarea',
    placeholder: 'State your actual availability clearly.',
  },
  {
    id: 'remoteSetup',
    label: 'Describe your current work setup in detail: laptop or desktop, backup power, headset, internet speed, and work environment.',
    type: 'textarea',
    placeholder: 'Explain the setup you would actually use.',
  },
  {
    id: 'englishUseAtWork',
    label: 'How often do you use English professionally today, and in what type of written or spoken situations?',
    type: 'textarea',
    placeholder: 'Email, calls, chat, operations notes, sales, support, etc.',
  },
  {
    id: 'roleFitStatement',
    label: 'Why should we believe you can do the role you applied for without needing constant supervision?',
    type: 'textarea',
    placeholder: 'Give concrete reasons, not motivational slogans.',
  },
];

const ENGLISH_QUESTIONS = [
  {
    id: 'englishIntro',
    label: 'Write a short professional self-introduction in English using 4 to 6 sentences.',
    type: 'textarea',
    placeholder: 'Write your answer in English.',
  },
  {
    id: 'englishDriverReply',
    label: 'A driver sends this message: "Truck problem. I will be late for pickup." Write a professional reply in English.',
    type: 'textarea',
    placeholder: 'Write the reply exactly as you would send it.',
  },
  {
    id: 'englishRewrite',
    label: 'Rewrite this sentence correctly in English: "He dont have the documents what you ask yesterday."',
    type: 'textarea',
    placeholder: 'Write the corrected sentence only.',
  },
  {
    id: 'englishBestSentence',
    label: 'Choose the best sentence.',
    type: 'radio',
    options: [
      'I reviewed your file and everything look good.',
      'I reviewed your file and everything looks good.',
      'I review your file and everything looked good.',
      'I reviewed your files and everything are good.',
    ],
    correctAnswer: 'I reviewed your file and everything looks good.',
  },
  {
    id: 'englishPreposition',
    label: 'Choose the correct sentence.',
    type: 'radio',
    options: [
      'Please send the report on Friday at 2 PM.',
      'Please send the report in Friday on 2 PM.',
      'Please send the report at Friday in 2 PM.',
      'Please send the report on Friday in 2 PM.',
    ],
    correctAnswer: 'Please send the report on Friday at 2 PM.',
  },
  {
    id: 'englishBestResponse',
    label: 'Choose the most professional customer-facing response.',
    type: 'radio',
    options: [
      'We cannot help. Try later.',
      'There is an issue. Wait.',
      'We are reviewing the issue now and will update you shortly.',
      'Not sure what happened.',
    ],
    correctAnswer: 'We are reviewing the issue now and will update you shortly.',
  },
  {
    id: 'englishGrammarChoice',
    label: 'Choose the grammatically correct sentence.',
    type: 'radio',
    options: [
      'The documents was already sent.',
      'The documents were already sent.',
      'The document were already sent.',
      'The documents is already sent.',
    ],
    correctAnswer: 'The documents were already sent.',
  },
  {
    id: 'englishEscalationChoice',
    label: 'Choose the strongest escalation note.',
    type: 'radio',
    options: [
      'Customer mad. Handle it.',
      'The customer is upset about the delay and is requesting an immediate update on the revised delivery time.',
      'Maybe there is a problem with delivery.',
      'Driver late. Customer waiting.',
    ],
    correctAnswer: 'The customer is upset about the delay and is requesting an immediate update on the revised delivery time.',
  },
  {
    id: 'englishEscalationWrite',
    label: 'Write a short escalation note in English to your manager about a missed pickup.',
    type: 'textarea',
    placeholder: 'Write the escalation note in English.',
  },
  {
    id: 'englishAvailabilityWrite',
    label: 'Write a short English message confirming that you are available for an interview tomorrow at 3 PM Eastern Time.',
    type: 'textarea',
    placeholder: 'Write the confirmation message in English.',
  },
];

function sanitizeText(value) {
  return String(value || '').trim();
}

function readAssessmentDraft() { return readApplicationDraft(CAREERS_ASSESSMENT_STORAGE_KEY); }
function clearAssessmentDraft() { clearApplicationDraft(readApplicationDraft()?.applicantReference); }

function buildInitialAnswers() {
  return [...EXPERIENCE_QUESTIONS, ...ENGLISH_QUESTIONS].reduce((acc, question) => {
    acc[question.id] = '';
    return acc;
  }, {});
}

export default function CareersAssessmentPage() {
  const ref = useReveal();
  const draft = useMemo(() => readAssessmentDraft(), []);
  const [answers, setAnswers] = useState(() => loadAssessmentAnswers(draft?.applicantReference, buildInitialAnswers()));
  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const updateAnswer = (id, value) => {
    setAnswers((current) => {
      const next = { ...current, [id]: value };
      saveAssessmentAnswers(draft?.applicantReference, next);
      return next;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!draft) {
      toast.error('No active application draft was found. Start from the careers page first.');
      return;
    }

    setIsSubmitting(true);

    const englishObjectiveTotal = ENGLISH_QUESTIONS.filter((question) => question.type === 'radio').length;
    const englishObjectiveCorrect = ENGLISH_QUESTIONS.filter((question) => {
      if (question.type !== 'radio') {
        return false;
      }
      return sanitizeText(answers[question.id]) === sanitizeText(question.correctAnswer);
    }).length;
    const totalAnswered = [...EXPERIENCE_QUESTIONS, ...ENGLISH_QUESTIONS].filter((question) => sanitizeText(answers[question.id])).length;

    const payload = {
      applicantReference: sanitizeText(draft.applicantReference),
      applicantName: sanitizeText(draft.initialForm?.name),
      applicantEmail: sanitizeText(draft.initialForm?.email),
      applicantPhone: sanitizeText(draft.initialForm?.phone),
      desiredRole: sanitizeText(draft.initialForm?.role),
      sourcePage: 'careers-assessment',
      initialApplication: draft.initialResult || null,
      initialForm: draft.initialForm || null,
      assessment: {
        submittedAt: new Date().toISOString(),
        sections: {
          experience: EXPERIENCE_QUESTIONS.map((question) => ({
            id: question.id,
            label: question.label,
            answer: sanitizeText(answers[question.id]),
          })),
          english: ENGLISH_QUESTIONS.map((question) => ({
            id: question.id,
            label: question.label,
            type: question.type,
            answer: sanitizeText(answers[question.id]),
            correctAnswer: question.type === 'radio' ? question.correctAnswer : undefined,
            isCorrect: question.type === 'radio' ? sanitizeText(answers[question.id]) === sanitizeText(question.correctAnswer) : undefined,
          })),
        },
        scores: {
          totalQuestions: EXPERIENCE_QUESTIONS.length + ENGLISH_QUESTIONS.length,
          totalAnswered,
          experienceAnswered: EXPERIENCE_QUESTIONS.filter((question) => sanitizeText(answers[question.id])).length,
          englishAnswered: ENGLISH_QUESTIONS.filter((question) => sanitizeText(answers[question.id])).length,
          englishObjectiveTotal,
          englishObjectiveCorrect,
        },
      },
    };

    try {
      const result = await submitJobApplicationAssessment(payload);
      if (result.success) {
        setSubmitted(true);
        clearAssessmentDraft();
        toast.success('Assessment submitted successfully');
      } else {
        toast.error('Failed to submit the assessment. Please try again.');
      }
    } catch (error) {
      toast.error('An unexpected error occurred.');
      console.error('Unexpected assessment error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!draft && !submitted) {
    return (
      <div ref={ref} className="min-h-screen bg-canvas flex items-center">
        <div className="site-content-shell w-full">
          <div className="border border-gold/20 bg-surface p-10 text-center">
            <CheckCircle2 className="w-12 h-12 text-accent-ink mx-auto mb-6" />
            <h1 className="font-display text-3xl text-ink mb-4">Start from the careers page</h1>
            <p className="font-body text-copy/50 max-w-xl mx-auto mb-8">
              Submit the base application before completing the assessment.
            </p>
            <FlowButton
              to="/work-with-us"
              className="inline-flex items-center gap-2   px-8 py-4 text-sm uppercase tracking-wider font-body font-bold  transition-colors"
            >
              Back to Work With Us
            </FlowButton>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div ref={ref} className="min-h-screen bg-canvas">
      <section className={PAGE_HERO_SECTION_CLASS}>
        <div className="absolute inset-0 bg-gradient-to-br from-canvas via-surface to-canvas" />
        <div className={PAGE_HERO_CONTAINER_CLASS}>
          <div className="max-w-4xl">
            <p className={PAGE_HERO_EYEBROW_CLASS}>Candidate assessment</p>
            <h1 className={PAGE_HERO_H1_CLASS}>
              Experience review and English screening
              <span className="shimmer-text">.</span>
            </h1>
            <p className={PAGE_HERO_BODY_TEXT_CLASS}>
              Complete the experience questions and English screening. Use real examples from your own work.
            </p>
          </div>
        </div>
      </section>

      <section className="py-24 md:py-32 bg-surface">
        <div className="site-content-shell">
          {submitted ? (
            <div className="border border-gold/20 bg-canvas p-12 text-center" data-testid="careers-assessment-success">
              <CheckCircle2 className="w-12 h-12 text-accent-ink mx-auto mb-6" />
              <h2 className="font-display text-3xl text-ink mb-4">Assessment Received</h2>
              <p className="font-body text-copy/50 max-w-2xl mx-auto mb-8">
                Your application and detailed assessment have been submitted. If you move forward, a Magellan team member will contact you by email.
              </p>
              <FlowButton
                to="/work-with-us"
                className="inline-flex items-center gap-2 border border-ink/20 text-ink px-8 py-4 text-sm uppercase tracking-wider font-body hover:border-gold  transition-colors"
              >
                Return to Work With Us
              </FlowButton>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-16" data-testid="careers-assessment-form">
              <div>
                <div className="mb-10">
                  <p className="text-xs uppercase tracking-[0.3em] text-accent-ink font-body mb-4">Section 01</p>
                  <h2 className="font-display text-3xl sm:text-4xl tracking-tight text-ink mb-4">Past experience</h2>
                  <p className="font-body text-copy/45 leading-relaxed max-w-3xl">
                    Tell us what you did, the tools you used and the decisions you handled.
                  </p>
                </div>

                <div className="space-y-10">
                  {EXPERIENCE_QUESTIONS.map((question, index) => (
                    <div key={question.id} className="border-b border-ink/10 pb-8">
                      <label className="text-sm text-copy/80 font-body leading-relaxed block mb-4">
                        <span className="text-accent-ink mr-3">{String(index + 1).padStart(2, '0')}</span>
                        {question.label}
                      </label>
                      <textarea
                        required
                        rows={4}
                        value={answers[question.id]}
                        onChange={(e) => updateAnswer(question.id, e.target.value)}
                        className="w-full bg-transparent border-b border-ink/20 text-ink py-3 px-0 focus:border-gold focus:outline-none transition-colors placeholder:text-ink/20 font-body resize-none"
                        placeholder={question.placeholder}
                      />
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <div className="mb-10">
                  <p className="text-xs uppercase tracking-[0.3em] text-accent-ink font-body mb-4">Section 02</p>
                  <h2 className="font-display text-3xl sm:text-4xl tracking-tight text-ink mb-4">English test</h2>
                  <p className="font-body text-copy/45 leading-relaxed max-w-3xl">
                    Answer in English. Follow each prompt as written.
                  </p>
                </div>

                <div className="space-y-10">
                  {ENGLISH_QUESTIONS.map((question, index) => (
                    <div key={question.id} className="border-b border-ink/10 pb-8">
                      <label className="text-sm text-copy/80 font-body leading-relaxed block mb-4">
                        <span className="text-accent-ink mr-3">{String(EXPERIENCE_QUESTIONS.length + index + 1).padStart(2, '0')}</span>
                        {question.label}
                      </label>

                      {question.type === 'radio' ? (
                        <div className="space-y-3">
                          {question.options.map((option) => (
                            <label key={option} className="flex items-start gap-3 cursor-pointer group">
                              <input
                                type="radio"
                                name={question.id}
                                value={option}
                                checked={answers[question.id] === option}
                                onChange={(e) => updateAnswer(question.id, e.target.value)}
                                className="mt-1 accent-[#4c8dcc]"
                                required
                              />
                              <span className="font-body text-copy/65 text-sm leading-relaxed group-hover:text-ink transition-colors">{option}</span>
                            </label>
                          ))}
                        </div>
                      ) : (
                        <textarea
                          required
                          rows={4}
                          value={answers[question.id]}
                          onChange={(e) => updateAnswer(question.id, e.target.value)}
                          className="w-full bg-transparent border-b border-ink/20 text-ink py-3 px-0 focus:border-gold focus:outline-none transition-colors placeholder:text-ink/20 font-body resize-none"
                          placeholder={question.placeholder}
                        />
                      )}
                    </div>
                  ))}
                </div>
              </div>

              <div className="border border-gold/20 bg-canvas p-8 md:p-10">
                <p className="text-xs uppercase tracking-[0.3em] text-accent-ink font-body mb-4">Submission</p>
                <h3 className="font-display text-2xl text-ink mb-4">Send the full screening packet</h3>
                <p className="font-body text-copy/45 leading-relaxed max-w-3xl mb-8">
                  After you submit this page, the complete packet is sent for review. If you are approved for the next stage, the team will continue by email.
                </p>
                <FlowButton
                  type="submit"
                  disabled={isSubmitting}
                  className="  px-10 py-4 text-sm uppercase tracking-wider font-body font-bold  transition-colors inline-flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? (
                    <>Processing <Loader2 className="w-4 h-4 animate-spin" /></>
                  ) : (
                    <>Submit Assessment </>
                  )}
                </FlowButton>
              </div>
            </form>
          )}
        </div>
      </section>
    </div>
  );
}

